// Late fees — client helpers (member handshake + admin lifecycle).
//
// A late fee is a SEPARATE member-level debt. This module reads late_fees + the
// append-only fee_events ledger and calls the three RPCs (add_late_fee,
// submit_fee_payment, resolve_late_fee). It NEVER touches contributions / pot /
// rounds — the engine cannot see these tables, and neither does any pot/summary
// calculation. State is DERIVED from the event log, mirroring public._fee_state.
import { supabaseClient } from './supabaseClient';

// Newest-first: created_at desc, id desc (matches the SQL tie-break).
const byNewest = (a, b) => {
  if (a.created_at !== b.created_at) return a.created_at < b.created_at ? 1 : -1;
  return Number(b.id) - Number(a.id);
};

// Derive a fee's state + the events that matter for display.
function deriveFee(fee, events) {
  const evs = [...events].sort(byNewest);
  const resolution = evs.find(e => e.event === 'paid' || e.event === 'waived') || null;
  const latest = evs[0] || null;
  let state;
  if (resolution) state = 'resolved';
  else if (latest && latest.event === 'submitted') state = 'pending';
  else state = 'outstanding';
  return {
    ...fee,
    events: evs,
    state,                                   // 'outstanding' | 'pending' | 'resolved'
    resolution,                              // the paid/waived event, if any
    submission: evs.find(e => e.event === 'submitted') || null,   // latest submission
    // A reject is only "active" when it's the most recent event (not superseded
    // by a resubmission) and the fee isn't resolved.
    rejected: state === 'outstanding' && latest?.event === 'rejected' ? latest : null,
  };
}

export function shapeFees(fees, events) {
  const byFee = new Map();
  (events || []).forEach(e => {
    const k = String(e.fee_id);
    if (!byFee.has(k)) byFee.set(k, []);
    byFee.get(k).push(e);
  });
  return (fees || []).map(f => deriveFee(f, byFee.get(String(f.id)) || []));
}

// Total still owed = every fee not yet resolved (OUTSTANDING + PENDING).
export const outstandingTotal = (shaped) =>
  (shaped || []).filter(f => f.state !== 'resolved').reduce((s, f) => s + Number(f.amount || 0), 0);

// Admin: all fees + events for a group (RLS scopes to the admin's groups).
export async function fetchGroupFees(groupId) {
  const gid = parseInt(groupId, 10);
  const [{ data: fees }, { data: events }] = await Promise.all([
    supabaseClient.from('late_fees').select('*').eq('group_id', gid).order('applied_at', { ascending: false }),
    supabaseClient.from('fee_events').select('*').eq('group_id', gid),
  ]);
  return shapeFees(fees, events);
}

// Member: own fees + events (RLS scopes to the member's own rows).
export async function fetchMemberFees(memberDbIds) {
  const ids = (memberDbIds || []).map(Number).filter(Boolean);
  if (!ids.length) return [];
  const [{ data: fees }, { data: events }] = await Promise.all([
    supabaseClient.from('late_fees').select('*').in('member_db_id', ids).order('applied_at', { ascending: false }),
    supabaseClient.from('fee_events').select('*').in('member_db_id', ids),
  ]);
  return shapeFees(fees, events);
}

// Admin: create a charge (RPC enforces admin-ownership + fees_enabled).
export async function addLateFee({ groupId, memberDbId, amount, note, cycleNumber = null, roundNumber = null }) {
  const { data, error } = await supabaseClient.rpc('add_late_fee', {
    p_group_id: parseInt(groupId, 10),
    p_member_db_id: memberDbId,
    p_amount: amount,
    p_note: note || null,
    p_cycle_number: cycleNumber,
    p_round_number: roundNumber,
  });
  if (error) throw new Error(error.message || 'Could not add the late fee.');
  return data;
}

// Member: "I paid the late fee" (RPC enforces the caller owns the fee's member).
export async function submitFeePayment({ feeId, method, reference, note }) {
  const { data, error } = await supabaseClient.rpc('submit_fee_payment', {
    p_fee_id: feeId,
    p_method: method || null,
    p_reference: reference || null,
    p_note: note || null,
  });
  if (error) throw new Error(error.message || 'Could not submit the fee payment.');
  return data;
}

// Admin: approve (paid) / waive / reject (RPC enforces group-admin + transitions).
export async function resolveLateFee({ feeId, resolution, note }) {
  const { data, error } = await supabaseClient.rpc('resolve_late_fee', {
    p_fee_id: feeId,
    p_resolution: resolution,
    p_note: note || null,
  });
  if (error) throw new Error(error.message || 'Could not update the late fee.');
  return data;
}
